'use client';

import { useCallback, useEffect, useState, type TouchEvent } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import MotionModal from '@/components/ui/motion-modal';
import { Button } from '@/components/ui/button';

interface LightboxProps {
  images: string[];
  startIndex?: number;
  onClose: () => void;
}

export function Lightbox({ images, startIndex = 0, onClose }: LightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const currentImage = images[currentIndex];
  const isGallery = images.length > 1;

  const minSwipeDistance = 50;

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setImageLoaded(false);
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setImageLoaded(false);
    }
  }, [currentIndex, images.length]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrevious, goToNext]);

  const onTouchStart = (e: TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Reset image loaded state when index changes
  useEffect(() => {
    setImageLoaded(false);
  }, [currentIndex]);

  return (
    <MotionModal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      animationStyle="from-center"
      viewTransitionName="repair-vt"
      contentClassName="max-w-5xl"
    >
      {/* Image counter for galleries */}
      {isGallery && (
        <div className="absolute top-4 left-4 z-50 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
          {currentIndex + 1} of {images.length}
        </div>
      )}

      {/* Main content area */}
      <div
        className="relative flex items-center justify-center min-h-[50vh] max-h-[95vh] w-full"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Previous button */}
        {isGallery && currentIndex > 0 && (
          <Button
            variant="ghost"
            size="lg"
            className="absolute left-4 z-40 bg-secondary"
            onClick={goToPrevious}
            aria-label="Previous image"
          >
            <ChevronLeft />
          </Button>
        )}

        {/* Next button */}
        {isGallery && currentIndex < images.length - 1 && (
          <Button
            variant="ghost"
            size="lg"
            className="absolute right-4 z-40 bg-secondary"
            onClick={goToNext}
            aria-label="Next image"
          >
            <ChevronRight />
          </Button>
        )}

        <div
          key={currentIndex}
          className="flex flex-col items-center justify-center max-w-full max-h-full relative"
        >
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          <Image
            width={600}
            height={600}
            src={currentImage}
            alt={`Image ${currentIndex + 1} of ${images.length}`}
            className={cn(
              'w-full h-full object-contain transition-opacity duration-300',
              imageLoaded ? 'opacity-100' : 'opacity-0',
            )}
            onLoad={() => setImageLoaded(true)}
            draggable={false}
          />
        </div>
      </div>
    </MotionModal>
  );
}
